import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { api, type InsightsOverviewSnapshot } from '@/lib/api'
import { isAuthenticated } from '@/lib/auth-store'
import styles from './home.module.css'

export const Route = createFileRoute('/insights')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: InsightsRoute,
})

function InsightsRoute() {
  const [snapshot, setSnapshot] = useState<InsightsOverviewSnapshot | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    const loadSnapshot = async (): Promise<void> => {
      try {
        const data = await api.getInsightsOverview()
        setSnapshot(data)
        setErrorMessage(null)
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : 'Failed to load analytics snapshot.'
        )
      }
    }

    void loadSnapshot()
  }, [])

  const performance = snapshot?.performanceSnapshot ?? {
    scheduled: 0,
    published: 0,
    draftQueue: 0,
    cadenceScore: 0,
  }

  const responders = snapshot?.responders.items ?? []
  const actions = snapshot?.recommendedActions.items ?? []

  return (
    <section className={styles.layout}>
      <header className={styles.hero}>
        <p className={styles.overline}>Insights</p>
        <h1 className={styles.pageTitle}>Know what worked and what to do next</h1>
        <p className={styles.subtitle}>
          Performance visibility, responder intelligence, and clear action cards designed to drive
          weekly execution.
        </p>
      </header>

      {errorMessage && <p className={styles.error}>{errorMessage}</p>}

      <section className={styles.analysisCard}>
        <h2>Performance snapshot</h2>
        <div className={styles.miniKpiGrid}>
          <div>
            <p className={styles.accountLabel}>Scheduled</p>
            <p className={styles.accountValue}>{performance.scheduled}</p>
          </div>
          <div>
            <p className={styles.accountLabel}>Published</p>
            <p className={styles.accountValue}>{performance.published}</p>
          </div>
          <div>
            <p className={styles.accountLabel}>Draft queue</p>
            <p className={styles.accountValue}>{performance.draftQueue}</p>
          </div>
          <div>
            <p className={styles.accountLabel}>Cadence score</p>
            <p className={styles.accountValue}>{performance.cadenceScore}%</p>
          </div>
        </div>
        <p className={styles.muted}>
          Current snapshot prioritizes actionable planning over deep dashboard configuration.
        </p>
      </section>

      <section className={styles.analysisCard}>
        <div className={styles.templatesHeader}>
          <h2>Who is responding to your content</h2>
          <p>{responders.length} active responders</p>
        </div>
        <div className={styles.templateList}>
          {responders.map((lead) => (
            <article key={lead.id} className={styles.templateRow}>
              <div>
                <h3>{lead.name}</h3>
                <p>
                  {lead.headline} at {lead.company}
                </p>
                <p>
                  {lead.signalSummary} • intent {lead.intent} • last activity {lead.lastEngagedAt}
                </p>
              </div>
              <Link to="/audience" className={styles.inlineLinkButton}>
                Review lead
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.analysisCard}>
        <div className={styles.templatesHeader}>
          <h2>Recommended next actions</h2>
          <p>{actions.length} recommendations</p>
        </div>
        <div className={styles.templateList}>
          {actions.map((card) => (
            <article key={card.id} className={styles.templateRow}>
              <div>
                <h3>{card.title}</h3>
                <p>{card.rationale}</p>
                <p className={styles.impactChip}>{card.impact}</p>
              </div>
              <Link to={card.actionPath} className={styles.inlineLinkButton}>
                {card.actionLabel}
              </Link>
            </article>
          ))}
        </div>
      </section>
    </section>
  )
}
